/**
 * WebMCP tools — exposes store actions to AI browsing agents.
 * Spec: https://developer.chrome.com/docs/ai/webmcp/imperative-api (Chrome docs, updated 2026-05-28)
 * EXPERIMENTAL: document.modelContext is a Chrome dev-trial API, subject to change.
 * Progressive enhancement: complete no-op in browsers without the API.
 */
(function () {
  "use strict";

  var mc = document.modelContext; // navigator.modelContext deprecated in Chrome 150
  if (!mc || typeof mc.registerTool !== "function") return; // feature-detect → no-op

  function money(cents) {
    return "₹" + (Number(cents) / 100).toFixed(2);
  }

  function loadRecommender() {
    return new Promise(function (resolve, reject) {
      if (window.ProductRecommender) return resolve(window.ProductRecommender);
      var src = window.WEBMCP_RECOMMENDER_SRC;
      if (!src) return reject(new Error("no src"));
      var existing = document.querySelector("script[data-webmcp-recommender]");
      if (existing) {
        existing.addEventListener("load", function () {
          resolve(window.ProductRecommender);
        });
        existing.addEventListener("error", function () {
          reject(new Error("load failed"));
        });
        return;
      }
      var s = document.createElement("script");
      s.src = src;
      s.defer = true;
      s.setAttribute("data-webmcp-recommender", "");
      s.onload = function () {
        resolve(window.ProductRecommender);
      };
      s.onerror = function () {
        reject(new Error("load failed"));
      };
      document.head.appendChild(s);
    });
  }

  // ---- get_cart (read-only) ----
  mc.registerTool({
    name: "get_cart",
    description:
      "Get the current shopping cart: line items, quantities, and total.",
    inputSchema: { type: "object", properties: {} },
    annotations: { readOnlyHint: true },
    execute: async function () {
      try {
        var res = await fetch("/cart.js", {
          headers: { Accept: "application/json" },
        });
        if (!res.ok)
          return "Could not read the cart (HTTP " + res.status + ").";
        var cart = await res.json();
        if (!cart.items || cart.items.length === 0) return "The cart is empty.";
        var lines = cart.items.map(function (i) {
          return (
            "- " +
            i.quantity +
            "× " +
            i.product_title +
            " (" +
            money(i.final_line_price) +
            ")"
          );
        });
        return (
          "Cart (" +
          cart.item_count +
          " items, total " +
          money(cart.total_price) +
          "):\n" +
          lines.join("\n")
        );
      } catch (e) {
        return "Could not read the cart right now.";
      }
    },
  });

  // ---- search_products (read-only) ----
  mc.registerTool({
    name: "search_products",
    description:
      "Search the catalog by keyword. Returns title, price, availability, product URL, and product_handle (pass product_handle to add_to_cart).",
    inputSchema: {
      type: "object",
      properties: {
        query: {
          type: "string",
          description: 'Keywords, e.g. "joint supplement" or "probiotic".',
        },
      },
      required: ["query"],
    },
    annotations: { readOnlyHint: true },
    execute: async function (args) {
      try {
        var q = encodeURIComponent((args && args.query) || "");
        var url =
          "/search/suggest.json?q=" +
          q +
          "&resources[type]=product&resources[limit]=6";
        var res = await fetch(url, { headers: { Accept: "application/json" } });
        if (!res.ok) return "Search failed (HTTP " + res.status + ").";
        var data = await res.json();
        var products = ((data.resources || {}).results || {}).products || [];
        if (!products.length)
          return 'No products found for "' + args.query + '".';
        return products
          .map(function (p) {
            return [
              p.title,
              p.price ? "· ₹" + p.price : "",
              "· " + (p.available ? "in stock" : "out of stock"),
              "· " + location.origin + (p.url || "").split("?")[0],
              "· product_handle=" + p.handle,
            ]
              .filter(Boolean)
              .join(" ");
          })
          .join("\n");
      } catch (e) {
        return "Could not search products right now.";
      }
    },
  });

  // ---- get_product_recommendations (read-only) ----
  mc.registerTool({
    name: "get_product_recommendations",
    description:
      "Recommend vet-formulated supplements for a pet based on health concerns (e.g. itching, digestion, joints, anxiety). Returns top products with product_handle for add_to_cart.",
    inputSchema: {
      type: "object",
      properties: {
        pet_type: {
          type: "string",
          enum: ["dog", "cat"],
          description: "Pet species.",
        },
        concerns: {
          type: "array",
          items: { type: "string" },
          description:
            'Health concern/symptom IDs, e.g. ["itching","joint-pain"].',
        },
      },
      required: ["concerns"],
    },
    annotations: { readOnlyHint: true },
    execute: async function (args) {
      try {
        var PR;
        try {
          PR = await loadRecommender();
        } catch (e) {
          PR = null;
        }
        if (!PR) return "Recommendation engine could not be loaded.";
        await PR.init();
        var concerns = (args && args.concerns) || [];
        if (!concerns.length) {
          var avail = (PR.getSymptoms && PR.getSymptoms()) || [];
          return (
            "Please provide at least one concern. Available: " +
            avail
              .map(function (s) {
                return s.id || s;
              })
              .join(", ")
          );
        }
        var recs = PR.getSymptomRecommendations(concerns) || [];
        if (!recs.length) return "No recommendations matched those concerns.";
        return recs
          .map(function (p, i) {
            return (
              i +
              1 +
              ". " +
              (p.name || p.id) +
              (p.subtitle ? " (" + p.subtitle + ")" : "") +
              (p.handle ? " · product_handle=" + p.handle : "") +
              (p.description ? " — " + p.description : "")
            );
          })
          .join("\n");
      } catch (e) {
        return "Could not generate recommendations right now.";
      }
    },
  });

  // ---- add_to_cart (state-changing — the only one) ----
  mc.registerTool({
    name: "add_to_cart",
    description:
      "Add a product to the cart. Provide variant_id (integer) OR product_handle (string). Opens the cart drawer on success.",
    inputSchema: {
      type: "object",
      properties: {
        variant_id: {
          type: "integer",
          description: "Shopify variant ID. Preferred when known.",
        },
        product_handle: {
          type: "string",
          description:
            "Product handle from search_products / get_product_recommendations. Resolved to its first available variant.",
        },
        quantity: {
          type: "integer",
          description: "Quantity to add (default 1).",
        },
      },
    },
    annotations: { readOnlyHint: false },
    execute: async function (args) {
      try {
        args = args || {};
        var qty = args.quantity && args.quantity > 0 ? args.quantity : 1;
        var variantId = args.variant_id;
        if (!variantId && args.product_handle) {
          var pr = await fetch(
            "/products/" + encodeURIComponent(args.product_handle) + ".js",
            { headers: { Accept: "application/json" } },
          );
          if (!pr.ok)
            return (
              'Could not find a product with handle "' +
              args.product_handle +
              '".'
            );
          var product = await pr.json();
          var variants = product.variants || [];
          if (variants.length > 1) {
            return (
              "This product has multiple options — call add_to_cart again with one of these variant_id values:\n" +
              variants
                .map(function (v) {
                  return (
                    "- variant_id=" +
                    v.id +
                    " — " +
                    v.title +
                    (v.available ? "" : " (out of stock)")
                  );
                })
                .join("\n")
            );
          }
          var variant =
            variants.filter(function (v) {
              return v.available;
            })[0] || variants[0];
          if (!variant) return "That product has no purchasable variant.";
          variantId = variant.id;
        }
        if (!variantId) return "Provide either variant_id or product_handle.";
        var res = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            id: variantId,
            quantity: qty,
            properties: { __webmcp: "1" },
          }),
        });
        if (!res.ok) {
          var err = await res.json().catch(function () {
            return {};
          });
          return (
            "Could not add to cart: " +
            (err.description || "HTTP " + res.status)
          );
        }
        var added = await res.json();
        try {
          // Mirror the theme's own post-add flow (see product-recommender.js):
          // notify the theme, then open the Shopflo cart drawer.
          document.dispatchEvent(new CustomEvent("cart:updated"));
          if (typeof window.handleFloCartBtn === "function") {
            window.handleFloCartBtn();
          }
        } catch (e) {}
        return (
          "Added " +
          qty +
          "× " +
          (added.product_title || "item") +
          (added.variant_title && added.variant_title !== "Default Title"
            ? " — " + added.variant_title
            : "") +
          " to the cart."
        );
      } catch (e) {
        return "Could not add to cart right now.";
      }
    },
  });
})();
